import * as Sentry from '@sentry/browser'

/**
 * Custom error class for user-facing error messages
 */
export class AppError extends Error {
	constructor(message: string) {
		super(message)
		this.name = 'AppError'
	}
}

/**
 * Handle errors and return user-friendly messages
 */
export async function handleError(error: unknown): Promise<string> {
	if (error instanceof AppError) return error.message;

	// 1. Extract the actual error data if it's a Supabase Function error
	let errorData: any = error;

	// Supabase SDK wraps non-2xx responses in a context property
	if (error && typeof error === 'object' && 'context' in error) {
		try {
			// The context is often a response object we need to parse
			errorData = await (error as any).context.json();
		} catch {
			errorData = error;
		}
	}

	// 2. Determine the user-friendly message
	const code = errorData?.code || errorData?.error?.code;
	const rawMessage = errorData?.message || errorData?.error || "";

	// Mapping codes to friendly messages
	const errorMap: Record<string, string> = {
		'user_already_exists': 'An account with this email already exists.',
		'email_exists': 'An account with this email already exists.',
		'invalid_credentials': 'Invalid email or password.',
		'email_not_confirmed': 'Please verify your email before signing in.',
		'23505': 'This record already exists (Unique constraint).', // Postgres code
	};

	if (code && errorMap[code]) {
		return errorMap[code];
	}

	// 3. Fallback logic for string-matching
	const msg = rawMessage.toLowerCase();
	if (msg.includes('network') || msg.includes('fetch')) {
		return 'Network error. Please check your connection.';
	}

	// 4. If the object came from our Edge Function's catch block, 
	// it usually has the "error" key. Since the wrapper already reported it, 
	// we skip Sentry.captureException here.
	const isHandledBackendError = errorData && typeof errorData === 'object' && 'error' in errorData;

	if (isHandledBackendError) {
		return 'A server error occurred. Our team has been notified.';
	}

	// 5. If it's not a user error and not a handled backend error, report it.
	Sentry.captureException(error, {
		extra: { errorData },
	});

	return rawMessage || 'An unexpected error occurred.';
}

/**
 * Throw an AppError (for user-facing errors)
 */
export function throwAppError(message: string): never {
	throw new AppError(message)
}
